import React from 'react';
import {type FieldPath, type FieldValues} from 'react-hook-form';
import type {SetRequired} from 'type-fest';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '../ReactHookFormControl';

export interface ReactHookFormTextFieldProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
	SetRequired<TextFieldProps, 'name'>,
	TFieldValues,
	TName
> {
	makeArray?: boolean;
}

export const ReactHookFormTextFieldPrimitive = <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		makeArray,
		ControllerProps,
		...props
	}: ReactHookFormTextFieldProps<TFieldValues, TName>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={props.name as TName}
			render={({field, formState}) => {
				// TODO: what is the proper way to deduce this with a field array?
				const fieldArrayPieces = props.name.split('.');
				let error = formState.errors[props.name]?.message?.toString();
				const fieldArrayIndex = parseInt(fieldArrayPieces[1]);
				if (fieldArrayPieces.length > 1 && !isNaN(fieldArrayIndex)) {
					const fieldArrayErrors = formState.errors[
						fieldArrayPieces[0]
					] as Array<any>;
					error = fieldArrayErrors?.[fieldArrayIndex]?.message?.toString();
				}
				return (
					<TextField
						error={error}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={ref}
						inputRef={combineRefs(props.inputRef, field.ref)}
						value={field.value ?? ''}
						onBlur={splitEventHandler(props.onBlur, field.onBlur)}
						onChange={useConstCallback(
							splitEventHandler(props.onChange, (event, value) =>
								field.onChange(
									makeArray ? (Array.isArray(value) ? value : [value]) : value,
								),
							),
						)}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` controlled `TextField` component
 */
export const ReactHookFormTextField = React.forwardRef(
	ReactHookFormTextFieldPrimitive,
) as <
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormTextFieldProps<TFieldValues, TName>,
) => JSX.Element;

export default ReactHookFormTextField;
